import { router } from 'expo-router'
import { useMemo, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardFooter, CardHeader } from '@/src/elements/card'
import { Chip } from '@/src/elements/chip'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { getRemainingIntentionTime } from '@/src/features/intention/utils/getRemainingIntentionTime'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { IntentionShareModal } from '../../../components/IntentionShareModal'
import { usePrettifyPrayers } from '../../../hooks/usePrettifyPrayers'
import { IntentionFormButtonPlaceholder } from './IntentionFormButtonPlaceholder'

type TIntentionFormButton = {
  theme?: string
}
export const IntentionFormButton = ({ theme }: TIntentionFormButton): ReactNode => {
  const { routes } = useRoutes()
  const { t } = useTranslate()
  const me = useMeQuery()
  const prettyPrayers = usePrettifyPrayers(me.data?.intention?.prayers)

  const intention = me.data?.intention
  const remainingTime = useMemo(() => intention && getRemainingIntentionTime(intention, Date.now()), [intention])

  if (me.isLoading) return <IntentionFormButtonPlaceholder />
  if (me.isError)
    return (
      <ErrorMessage error={me.error} defaultErrorKey="common.error.unknown" testID={TEST_ID.INTENTIONS.FORM.ERROR} />
    )

  const handleIntentionBlockPress = () => {
    router.navigate(routes.intentionsForm(theme))
  }

  const handlePrayersPress = () => {
    if (me.data?.intention?.id) {
      router.navigate(routes.intentionByIdPrayers(me.data.intention.id, INTENTION_TYPE.INTENTION))
    }
  }

  return (
    <>
      {!!me.data?.intention && (
        <Card className="w-full border border-layout-stroke-contrast-low p-0" shadow="md">
          <CardHeader
            testID={TEST_ID.INTENTIONS.FORM.CARD_PRESS}
            onPress={handleIntentionBlockPress}
            className="flex-row justify-between px-4 pt-4"
          >
            <Typography bold contrast="low">
              <FormattedMessage id="words.your-intention" />
            </Typography>
            <Icon className="text-default-text-contrast-low" name={ICON.PENCIL} size="sm" />
          </CardHeader>
          <CardBody onPress={handleIntentionBlockPress} className="px-4 py-2">
            <Typography contrast="high">{me.data.intention.content}</Typography>
          </CardBody>
          <CardFooter className="flex-row items-center gap-2 px-4 pb-4">
            <View className="flex flex-1 flex-row items-start gap-2">
              <Chip colors="default" text={t('intention.time-left', { count: remainingTime ?? 0 })} variant="flat" />
              {me.data.intention.prayersCount > 0 && (
                <View className="flex flex-1 flex-row items-start">
                  <Chip
                    badgeInfo={me.data.intention.prayersCount}
                    colors="primary"
                    iconName={ICON.FIRE}
                    onPress={handlePrayersPress}
                    testID={TEST_ID.INTENTIONS.FORM.PRAYERS_LIST}
                    text={prettyPrayers.all}
                    variant="flat"
                  />
                </View>
              )}
            </View>
            <IntentionShareModal
              intention={me.data.intention}
              intentionId={me.data.intention.id}
              shareToken={me.data.intention.shareToken}
              trackingSource={TRACKING_SOURCE.INTENTION_PAGE}
            >
              {({ isLoading, onShare }) => (
                <Button
                  isIconOnly
                  startIcon={ICON.SHARE}
                  contrast="low"
                  colors="default"
                  variant="light"
                  onPress={onShare}
                  isDisabled={isLoading}
                  size="xs"
                  testID={TEST_ID.INTENTION.SHARE.BUTTON}
                />
              )}
            </IntentionShareModal>
          </CardFooter>
        </Card>
      )}
      {!me.data?.intention && (
        <Card isPressable onPress={handleIntentionBlockPress} shadow="md" className="w-full">
          <CardBody className="bg-default-bg-low flex-row items-center justify-between gap-4 bg-default-bg-light p-4">
            <Typography bold contrast="low">
              <FormattedMessage id="intention.publish.button" />
            </Typography>
            <Icon className="text-default-text-contrast-low" name={ICON.PENCIL} size="sm" />
          </CardBody>
        </Card>
      )}
    </>
  )
}
